import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { Moment } from 'moment';
import { ConfigService } from '@app/shared/auth/config.service';
import { DatePeriod } from '@app/shared/components/date-presets-selector/interfaces/date-presets-selector.interface';
import {
  CompareDateRangeInterface,
  DateRangeInterface,
} from '@app/shared/components/date-picker/interfaces/date-picker.interface';
import { DateRange } from '@app/shared/services/date/date.interface';

@Injectable()
export class DateService {
  private reportsConfig;
  private weekType: 'week' | 'isoWeek';

  constructor(private configService: ConfigService) {
    this.reportsConfig = this.configService.getClientConfig('reports');
    this.weekType = this.reportsConfig.nonStandardWeek ? 'week' : 'isoWeek';
  }

  public getRange(period: DatePeriod): DateRangeInterface {
    const thisWeekEnd = moment().subtract(1, 'day').isBefore(moment().startOf(this.weekType))
      ? moment().startOf(this.weekType)
      : moment().subtract(1, 'day');

    const thisMonthEnd = moment().subtract(1, 'day').isBefore(moment().startOf('month'))
      ? moment().startOf('month')
      : moment().subtract(1, 'day');
    const thisYearEnd = moment().subtract(1, 'day').isBefore(moment().startOf('year'))
      ? moment().startOf('year')
      : moment().subtract(1, 'day');
    switch (period) {
      case DatePeriod.YESTERDAY:
        return {
          start: moment().subtract(1, 'days').startOf('day').format('YYYY-MM-DD'),
          end: moment().subtract(1, 'days').endOf('day').format('YYYY-MM-DD'),
        };
      case DatePeriod.THIS_WEEK:
        return {
          start: moment().startOf(this.weekType).format('YYYY-MM-DD'),
          end: thisWeekEnd.format('YYYY-MM-DD'),
        };
      case DatePeriod.LAST_WEEK:
        return {
          start: moment().subtract(1, 'week').startOf(this.weekType).format('YYYY-MM-DD'),
          end: moment().subtract(1, 'week').endOf(this.weekType).format('YYYY-MM-DD'),
        };
      case DatePeriod.THIS_MONTH:
        return {
          start: moment().startOf('month').format('YYYY-MM-DD'),
          end: thisMonthEnd.format('YYYY-MM-DD'),
        };
      case DatePeriod.LAST_MONTH:
        return {
          start: moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
          end: moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
        };
      case DatePeriod.LAST_30_DAYS:
        return {
          start: moment().subtract(30, 'days').format('YYYY-MM-DD'),
          end: moment().subtract(1, 'days').format('YYYY-MM-DD'),
        };
      case DatePeriod.ROLLING_12_MONTHS:
        return {
          start: moment().subtract(1, 'day').subtract(1, 'year').startOf('day').format('YYYY-MM-DD'),
          end: moment().subtract(1, 'day').endOf('day').format('YYYY-MM-DD'),
        };
      case DatePeriod.THIS_YEAR:
        return {
          start: moment().startOf('year').format('YYYY-MM-DD'),
          end: thisYearEnd.format('YYYY-MM-DD'),
        };
    }
  }

  public getCompareRange(period: DatePeriod): CompareDateRangeInterface {
    const thisWeekEnd = moment().subtract(1, 'day').isBefore(moment().startOf(this.weekType))
      ? moment().startOf(this.weekType)
      : moment().subtract(1, 'day');

    const thisMonthEnd = moment().subtract(1, 'day').isBefore(moment().startOf('month'))
      ? moment().startOf('month')
      : moment().subtract(1, 'day');
    const thisYearEnd = moment().subtract(1, 'day').isBefore(moment().startOf('year'))
      ? moment().startOf('year')
      : moment().subtract(1, 'day');
    switch (period) {
      case DatePeriod.YESTERDAY:
        return {
          compareStart: moment().subtract(1, 'days').startOf('day').clone().subtract(1, 'days').format('YYYY-MM-DD'),
          compareEnd: moment().subtract(1, 'days').endOf('day').clone().subtract(1, 'days').format('YYYY-MM-DD'),
        };
      case DatePeriod.THIS_WEEK:
        return this.getThisWeekLastYearSamePeriodRange({
          startDate: moment().startOf(this.weekType),
          endDate: thisWeekEnd,
        });
      case DatePeriod.LAST_WEEK:
        return this.getLastWeekLastYearSamePeriodRange({
          startDate: moment().subtract(1, 'week').startOf(this.weekType),
          endDate: moment().subtract(1, 'week').endOf(this.weekType),
        });
      case DatePeriod.THIS_MONTH:
        return this.getPreviousYearSamePeriodRange({
          startDate: moment().startOf('month'),
          endDate: thisMonthEnd,
        });
      case DatePeriod.LAST_MONTH:
        return this.getPreviousYearSamePeriodRange({
          startDate: moment().subtract(1, 'month').startOf('month'),
          endDate: moment().subtract(1, 'month').endOf('month'),
        });
      case DatePeriod.LAST_30_DAYS:
        return this.getPreviousYearSamePeriodRange({
          startDate: moment().subtract(30, 'days'),
          endDate: moment().subtract(1, 'days'),
        });
      case DatePeriod.ROLLING_12_MONTHS:
        return this.getPreviousYearSamePeriodRange({
          startDate: moment().subtract(1, 'day').subtract(1, 'year').startOf('day'),
          endDate: moment().subtract(1, 'day').endOf('day'),
        });
      case DatePeriod.THIS_YEAR:
        return this.getPreviousYearSamePeriodRange({
          startDate: moment().startOf('year'),
          endDate: thisYearEnd,
        });
    }
  }

  public getCompareRangeM1(period: DatePeriod): CompareDateRangeInterface {
    const thisWeekEnd = moment().subtract(1, 'day').isBefore(moment().startOf(this.weekType))
      ? moment().startOf(this.weekType)
      : moment().subtract(1, 'day');

    const thisMonthEnd = moment().subtract(1, 'day').isBefore(moment().startOf('month'))
      ? moment().startOf('month')
      : moment().subtract(1, 'day');

    const thisYearEnd = moment().subtract(1, 'day').isBefore(moment().startOf('month'))
      ? moment().startOf('month')
      : moment().subtract(1, 'day');
    switch (period) {
      case DatePeriod.THIS_WEEK:
        return this.getThisWeekLastYearM1SamePeriodRange({
          startDate: moment().startOf(this.weekType),
          endDate: thisWeekEnd,
        });
      case DatePeriod.LAST_WEEK:
        return this.getLastWeekLastYearM1SamePeriodRange({
          startDate: moment().subtract(1, 'week').startOf(this.weekType),
          endDate: moment().subtract(1, 'week').endOf(this.weekType),
        });
      case DatePeriod.THIS_MONTH:
        return this.getPreviousYearM1SamePeriodRange({
          startDate: moment().startOf('month'),
          endDate: thisMonthEnd,
        });
      case DatePeriod.LAST_MONTH:
        return this.getPreviousYearM1SamePeriodRange({
          startDate: moment().subtract(1, 'month').startOf('month'),
          endDate: moment().subtract(1, 'month').endOf('month'),
        });
      case DatePeriod.LAST_30_DAYS:
        return this.getPreviousYearM1SamePeriodRange({
          startDate: moment().subtract(30, 'days'),
          endDate: moment().subtract(1, 'days'),
        });
      case DatePeriod.ROLLING_12_MONTHS:
        return this.getPreviousYearM1SamePeriodRange({
          startDate: moment().subtract(1, 'day').subtract(1, 'year').startOf('day'),
          endDate: moment().subtract(1, 'day').endOf('day'),
        });
      case DatePeriod.THIS_YEAR:
        return this.getPreviousYearM1SamePeriodRange({
          startDate: moment().startOf('year'),
          endDate: thisYearEnd,
        });
    }
  }

  public getCompareRangeM2(period: DatePeriod): CompareDateRangeInterface {
    const thisWeekEnd = moment().subtract(1, 'day').isBefore(moment().startOf(this.weekType))
      ? moment().startOf(this.weekType)
      : moment().subtract(1, 'day');

    const thisMonthEnd = moment().subtract(1, 'day').isBefore(moment().startOf('month'))
      ? moment().startOf('month')
      : moment().subtract(1, 'day');

    const thisYearEnd = moment().subtract(1, 'day').isBefore(moment().startOf('month'))
      ? moment().startOf('month')
      : moment().subtract(1, 'day');
    switch (period) {
      case DatePeriod.THIS_WEEK:
        return this.getThisWeekLastYearM2SamePeriodRange({
          startDate: moment().startOf(this.weekType),
          endDate: thisWeekEnd,
        });
      case DatePeriod.LAST_WEEK:
        return this.getLastWeekLastYearM2SamePeriodRange({
          startDate: moment().subtract(1, 'week').startOf(this.weekType),
          endDate: moment().subtract(1, 'week').endOf(this.weekType),
        });
      case DatePeriod.THIS_MONTH:
        return this.getPreviousYearM2SamePeriodRange({
          startDate: moment().startOf('month'),
          endDate: thisMonthEnd,
        });
      case DatePeriod.LAST_MONTH:
        return this.getPreviousYearM2SamePeriodRange({
          startDate: moment().subtract(1, 'month').startOf('month'),
          endDate: moment().subtract(1, 'month').endOf('month'),
        });
      case DatePeriod.LAST_30_DAYS:
        return this.getPreviousYearM2SamePeriodRange({
          startDate: moment().subtract(30, 'days'),
          endDate: moment().subtract(1, 'days'),
        });
      case DatePeriod.ROLLING_12_MONTHS:
        return this.getPreviousYearM2SamePeriodRange({
          startDate: moment().subtract(1, 'day').subtract(1, 'year').startOf('day'),
          endDate: moment().subtract(1, 'day').endOf('day'),
        });
      case DatePeriod.THIS_YEAR:
        return this.getPreviousYearM2SamePeriodRange({
          startDate: moment().startOf('year'),
          endDate: thisYearEnd,
        });
    }
  }


  private getLastWeekLastYearSamePeriodRange(basePeriod: DateRange): CompareDateRangeInterface {
    const weekNumber: number = this.reportsConfig.nonStandardWeek
      ? basePeriod.startDate.week()
      : basePeriod.startDate.isoWeek();
    const prevYear: number = basePeriod.startDate.year() - 1;
    const start: Moment = this.reportsConfig.nonStandardWeek
      ? moment().year(prevYear).week(weekNumber).startOf(this.weekType)
      : moment().year(prevYear).isoWeek(weekNumber).startOf(this.weekType);
    const end: Moment = this.reportsConfig.nonStandardWeek
      ? moment().year(prevYear).week(weekNumber).endOf(this.weekType)
      : moment().year(prevYear).isoWeek(weekNumber).endOf(this.weekType);

    return {
      compareStart: start.format('YYYY-MM-DD'),
      compareEnd: end.format('YYYY-MM-DD'),
    };
  }

  private getLastWeekLastYearM1SamePeriodRange(basePeriod: DateRange): CompareDateRangeInterface {
    const weekNumber: number = this.reportsConfig.nonStandardWeek
      ? basePeriod.startDate.week()
      : basePeriod.startDate.isoWeek();
    const prevYear: number = basePeriod.startDate.year() - 2;
    const start: Moment = this.reportsConfig.nonStandardWeek
      ? moment().year(prevYear).week(weekNumber).startOf(this.weekType)
      : moment().year(prevYear).isoWeek(weekNumber).startOf(this.weekType);
    const end: Moment = this.reportsConfig.nonStandardWeek
      ? moment().year(prevYear).week(weekNumber).endOf(this.weekType)
      : moment().year(prevYear).isoWeek(weekNumber).endOf(this.weekType);

    return {
      compareStart: start.format('YYYY-MM-DD'),
      compareEnd: end.format('YYYY-MM-DD'),
    };
  }

  private getLastWeekLastYearM2SamePeriodRange(basePeriod: DateRange): CompareDateRangeInterface {
    const weekNumber: number = this.reportsConfig.nonStandardWeek
      ? basePeriod.startDate.week()
      : basePeriod.startDate.isoWeek();
    const prevYear: number = basePeriod.startDate.year() - 3;
    const start: Moment = this.reportsConfig.nonStandardWeek
      ? moment().year(prevYear).week(weekNumber).startOf(this.weekType)
      : moment().year(prevYear).isoWeek(weekNumber).startOf(this.weekType);
    const end: Moment = this.reportsConfig.nonStandardWeek
      ? moment().year(prevYear).week(weekNumber).endOf(this.weekType)
      : moment().year(prevYear).isoWeek(weekNumber).endOf(this.weekType);

    return {
      compareStart: start.format('YYYY-MM-DD'),
      compareEnd: end.format('YYYY-MM-DD'),
    };
  }

  private getPreviousYearSamePeriodRange(basePeriod: DateRange): CompareDateRangeInterface {
    const start: Moment = basePeriod.startDate.clone().subtract(1, 'year');
    const end: Moment = basePeriod.endDate.clone().subtract(1, 'year');

    return {
      compareStart: start.format('YYYY-MM-DD'),
      compareEnd: end.format('YYYY-MM-DD'),
    };
  }

  private getPreviousYearM1SamePeriodRange(basePeriod: DateRange): CompareDateRangeInterface {
    const start: Moment = basePeriod.startDate.clone().subtract(2, 'year');
    const end: Moment = basePeriod.endDate.clone().subtract(2, 'year');

    return {
      compareStart: start.format('YYYY-MM-DD'),
      compareEnd: end.format('YYYY-MM-DD'),
    };
  }

  private getPreviousYearM2SamePeriodRange(basePeriod: DateRange): CompareDateRangeInterface {
    const start: Moment = basePeriod.startDate.clone().subtract(3, 'year');
    const end: Moment = basePeriod.endDate.clone().subtract(3, 'year');

    return {
      compareStart: start.format('YYYY-MM-DD'),
      compareEnd: end.format('YYYY-MM-DD'),
    };
  }

  private getThisWeekLastYearSamePeriodRange(basePeriod: DateRange): CompareDateRangeInterface {
    const prevYear: number = basePeriod.startDate.year() - 1;
    const weekNumber: number = this.reportsConfig.nonStandardWeek
      ? basePeriod.startDate.week()
      : basePeriod.startDate.isoWeek();
    const diff: number = basePeriod.endDate.diff(basePeriod.startDate, 'day');
    const start: Moment = this.reportsConfig.nonStandardWeek
      ? moment().year(prevYear).week(weekNumber).startOf(this.weekType)
      : moment().year(prevYear).isoWeek(weekNumber).startOf(this.weekType);
    const end: Moment = start.clone().add(diff, 'days');

    return {
      compareStart: start.format('YYYY-MM-DD'),
      compareEnd: end.format('YYYY-MM-DD'),
    };
  }

  private getThisWeekLastYearM1SamePeriodRange(basePeriod: DateRange): CompareDateRangeInterface {
    const prevYear: number = basePeriod.startDate.year() - 2;
    const weekNumber: number = this.reportsConfig.nonStandardWeek
      ? basePeriod.startDate.week()
      : basePeriod.startDate.isoWeek();
    const diff: number = basePeriod.endDate.diff(basePeriod.startDate, 'day');
    const start: Moment = this.reportsConfig.nonStandardWeek
      ? moment().year(prevYear).week(weekNumber).startOf(this.weekType)
      : moment().year(prevYear).isoWeek(weekNumber).startOf(this.weekType);
    const end: Moment = start.clone().add(diff, 'days');

    return {
      compareStart: start.format('YYYY-MM-DD'),
      compareEnd: end.format('YYYY-MM-DD'),
    };
  }

  private getThisWeekLastYearM2SamePeriodRange(basePeriod: DateRange): CompareDateRangeInterface {
    const prevYear: number = basePeriod.startDate.year() - 3;
    const weekNumber: number = this.reportsConfig.nonStandardWeek
      ? basePeriod.startDate.week()
      : basePeriod.startDate.isoWeek();
    const diff: number = basePeriod.endDate.diff(basePeriod.startDate, 'day');
    const start: Moment = this.reportsConfig.nonStandardWeek
      ? moment().year(prevYear).week(weekNumber).startOf(this.weekType)
      : moment().year(prevYear).isoWeek(weekNumber).startOf(this.weekType);
    const end: Moment = start.clone().add(diff, 'days');

    return {
      compareStart: start.format('YYYY-MM-DD'),
      compareEnd: end.format('YYYY-MM-DD'),
    };
  }
}
